/**
 * Analytics Tracking Hooks
 * Custom hooks for various tracking scenarios
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/lib/analytics';

/**
 * Track scroll depth (25%, 50%, 75%, 100%)
 */
export function useScrollTracking(): void {
  const location = useLocation();
  const trackedDepths = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Reset tracked depths on route change
    trackedDepths.current = new Set();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) {return;}

      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
      const depths = [25, 50, 75, 100];

      depths.forEach(depth => {
        if (scrollPercent >= depth && !trackedDepths.current.has(depth)) {
          trackedDepths.current.add(depth);
          analytics.trackScrollDepth(depth);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
}

/**
 * Track button/link clicks
 */
export function useClickTracking(): void {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button, a[href], [role="button"]') as HTMLElement | null;
      
      if (!button) {return;}

      const isButton = button.tagName === 'BUTTON' || button.getAttribute('role') === 'button';
      const isLink = button.tagName === 'A';
      
      if (isButton) {
        const text = button.textContent?.trim().slice(0, 50) || 'unknown';
        const id = button.id || button.getAttribute('data-track') || undefined;
        
        analytics.trackCTAClick(text, id);
      } else if (isLink) {
        const href = (button as HTMLAnchorElement).href;
        const isOutbound = href && !href.includes(window.location.hostname);
        
        if (isOutbound) {
          analytics.trackOutboundLink(href, button.textContent?.trim());
        }
      }
    };

    document.addEventListener('click', handleClick, { passive: true });
    return () => document.removeEventListener('click', handleClick);
  }, []);
}

/**
 * Track form submissions
 */
export function useFormTracking(formName: string): {
  trackSubmit: (success: boolean) => void;
} {
  const trackSubmit = useCallback((success: boolean) => {
    analytics.trackFormSubmission(formName, success);
  }, [formName]);

  return { trackSubmit };
}

/**
 * Track form abandonment
 */
export function useFormAbandonmentTracking(
  formName: string,
  getFilledFields: () => string[]
): void {
  const hasInteracted = useRef(false);
  const submitted = useRef(false);

  useEffect(() => {
    const handleFocus = () => {
      hasInteracted.current = true;
    };

    const handleSubmit = () => {
      submitted.current = true;
    };

    const handleBeforeUnload = () => {
      if (hasInteracted.current && !submitted.current) {
        const filledFields = getFilledFields();
        if (filledFields.length > 0) {
          analytics.trackFormAbandonment(formName, filledFields);
        }
      }
    };

    // Track when user starts interacting with form
    document.addEventListener('focusin', handleFocus, { passive: true });
    document.addEventListener('submit', handleSubmit, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('submit', handleSubmit);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Also track on unmount (route change)
      if (hasInteracted.current && !submitted.current) {
        const filledFields = getFilledFields();
        if (filledFields.length > 0) {
          analytics.trackFormAbandonment(formName, filledFields);
        }
      }
    };
  }, [formName, getFilledFields]);

  return;
}

/**
 * Track time spent on page
 */
export function useTimeOnPage(): void {
  const location = useLocation();
  const startTime = useRef(Date.now());

  useEffect(() => {
    startTime.current = Date.now();

    return () => {
      const seconds = Math.round((Date.now() - startTime.current) / 1000);
      if (seconds > 0) {
        analytics.trackTimeOnPage(seconds);
      }
    };
  }, [location.pathname]);
}

/**
 * Track outbound link clicks
 */
export function useOutboundLinkTracking(): void {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      
      if (!link) {return;}

      const href = link.href;
      const isOutbound = href && 
        !href.startsWith(window.location.origin) && 
        !href.startsWith('/') &&
        !href.startsWith('#');

      if (isOutbound) {
        analytics.trackOutboundLink(href, link.textContent?.trim());
      }
    };

    document.addEventListener('click', handleClick, { passive: true });
    return () => document.removeEventListener('click', handleClick);
  }, []);
}

/**
 * Track page views on route change
 */
export function usePageViewTracking(): void {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location.pathname]);
}

/**
 * Combined hook for all tracking (use in layout component)
 */
export function useAnalytics(): void {
  usePageViewTracking();
  useScrollTracking();
  useClickTracking();
  useTimeOnPage();
  useOutboundLinkTracking();
}
